/**
 * Scoring System
 * Manages the four-dimensional scoring: brain, heart, task, communication
 * Based on game_model.yaml scoring section
 */

import { PlayerState, PlayerScores } from '../models/Player';
import { QuestionId, QUESTIONS } from '../models/Question';

export class ScoringSystem {
  /**
   * Apply scoring for a player's answer to a question
   */
  applyScore(
    player: PlayerState,
    questionId: QuestionId,
    answer: string
  ): PlayerScores {
    const question = QUESTIONS[questionId];

    if (!question.scoring) {
      // No scoring for this question
      return player.scores;
    }

    let scoreDeltas: PlayerScores | undefined;

    // Check for role-specific scoring
    if (typeof question.scoring === 'object' && 'default' in question.scoring) {
      // Role-specific scoring (e.g., Q7 for FIXER)
      const scoringObj = question.scoring as Record<string, Record<string, PlayerScores>>;
      if (player.role in scoringObj) {
        const roleScoring = scoringObj[player.role];
        if (roleScoring && typeof roleScoring === 'object' && answer in roleScoring) {
          scoreDeltas = roleScoring[answer] as PlayerScores;
        }
      }

      // Fallback to default scoring
      if (!scoreDeltas && scoringObj.default && answer in scoringObj.default) {
        scoreDeltas = scoringObj.default[answer] as PlayerScores;
      }
    } else if (typeof question.scoring === 'object' && answer in question.scoring) {
      // Standard scoring
      scoreDeltas = question.scoring[answer];
    }

    if (!scoreDeltas) {
      console.warn(`No score deltas found for ${questionId} answer ${answer}`);
      return player.scores;
    }

    // Apply deltas to current scores
    return {
      brain: player.scores.brain + (scoreDeltas.brain || 0),
      heart: player.scores.heart + (scoreDeltas.heart || 0),
      task: player.scores.task + (scoreDeltas.task || 0),
      communication: player.scores.communication + (scoreDeltas.communication || 0)
    };
  }

  /**
   * Calculate total score for a player
   */
  calculateTotalScore(scores: PlayerScores): number {
    return scores.brain + scores.heart + scores.task + scores.communication;
  }

  /**
   * Get score category label
   */
  getScoreCategory(totalScore: number): string {
    if (totalScore >= 40) return 'Exceptional Leader';
    if (totalScore >= 30) return 'Strong Leader';
    if (totalScore >= 20) return 'Developing Leader';
    if (totalScore >= 10) return 'Emerging Leader';
    return 'Learning Leader';
  }

  /**
   * Get dimension breakdown percentages
   */
  getScoreBreakdown(scores: PlayerScores): {
    brain: number;
    heart: number;
    task: number;
    communication: number;
  } {
    const total = this.calculateTotalScore(scores);

    if (total === 0) {
      return { brain: 25, heart: 25, task: 25, communication: 25 };
    }

    return {
      brain: Math.round((scores.brain / total) * 100),
      heart: Math.round((scores.heart / total) * 100),
      task: Math.round((scores.task / total) * 100),
      communication: Math.round((scores.communication / total) * 100)
    };
  }

  /**
   * Get leadership style based on dominant dimension
   */
  getLeadershipStyle(scores: PlayerScores): string {
    const max = Math.max(scores.brain, scores.heart, scores.task, scores.communication);

    if (scores.brain === max) {
      return 'Analytical Leader (Brain-focused)';
    }
    if (scores.heart === max) {
      return 'Empathetic Leader (Heart-focused)';
    }
    if (scores.task === max) {
      return 'Results-Oriented Leader (Task-focused)';
    }
    if (scores.communication === max) {
      return 'Collaborative Leader (Communication-focused)';
    }

    return 'Balanced Leader';
  }

  /**
   * Calculate team average scores
   */
  calculateTeamScores(players: PlayerState[]): PlayerScores {
    if (players.length === 0) {
      return { brain: 0, heart: 0, task: 0, communication: 0 };
    }

    const total = players.reduce(
      (acc, player) => ({
        brain: acc.brain + player.scores.brain,
        heart: acc.heart + player.scores.heart,
        task: acc.task + player.scores.task,
        communication: acc.communication + player.scores.communication
      }),
      { brain: 0, heart: 0, task: 0, communication: 0 }
    );

    return {
      brain: Math.round(total.brain / players.length),
      heart: Math.round(total.heart / players.length),
      task: Math.round(total.task / players.length),
      communication: Math.round(total.communication / players.length)
    };
  }

  /**
   * Rank players by total score
   */
  rankPlayers(players: PlayerState[]): PlayerState[] {
    return [...players].sort((a, b) => {
      const scoreA = this.calculateTotalScore(a.scores);
      const scoreB = this.calculateTotalScore(b.scores);
      return scoreB - scoreA; // Descending order
    });
  }

  /**
   * Get player's rank
   */
  getPlayerRank(player: PlayerState, allPlayers: PlayerState[]): number {
    const ranked = this.rankPlayers(allPlayers);
    return ranked.findIndex(p => p.playerId === player.playerId) + 1;
  }

  /**
   * Calculate score growth from question
   */
  getScoreGrowth(
    beforeScores: PlayerScores,
    afterScores: PlayerScores
  ): PlayerScores {
    return {
      brain: afterScores.brain - beforeScores.brain,
      heart: afterScores.heart - beforeScores.heart,
      task: afterScores.task - beforeScores.task,
      communication: afterScores.communication - beforeScores.communication
    };
  }

  /**
   * Generate score summary for player
   */
  generateScoreSummary(player: PlayerState): {
    scores: PlayerScores;
    total: number;
    category: string;
    leadershipStyle: string;
    breakdown: {
      brain: number;
      heart: number;
      task: number;
      communication: number;
    };
  } {
    const total = this.calculateTotalScore(player.scores);

    return {
      scores: player.scores,
      total,
      category: this.getScoreCategory(total),
      leadershipStyle: this.getLeadershipStyle(player.scores),
      breakdown: this.getScoreBreakdown(player.scores)
    };
  }

  /**
   * Generate team score summary
   */
  generateTeamSummary(players: PlayerState[]): {
    teamScores: PlayerScores;
    teamTotal: number;
    teamCategory: string;
    playerSummaries: Array<{
      scores: PlayerScores;
      total: number;
      category: string;
      leadershipStyle: string;
      breakdown: {
        brain: number;
        heart: number;
        task: number;
        communication: number;
      };
    }>;
    rankings: PlayerState[];
  } {
    const teamScores = this.calculateTeamScores(players);
    const teamTotal = this.calculateTotalScore(teamScores);

    return {
      teamScores,
      teamTotal,
      teamCategory: this.getScoreCategory(teamTotal),
      playerSummaries: players.map(p => this.generateScoreSummary(p)),
      rankings: this.rankPlayers(players)
    };
  }
}

// Singleton instance
export const scoringSystem = new ScoringSystem();
